import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { AdminSection, hasPermission } from '@/lib/permissions';

interface AdminRouteProps {
  children: React.ReactNode;
  requiredSection?: AdminSection;
}

const AdminRoute = ({ children, requiredSection }: AdminRouteProps) => {
  const { user, isAdmin, role, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not logged in - redirect to admin login
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Logged in but not an admin role
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Check section-specific permission if required
  if (requiredSection && !hasPermission(role, requiredSection)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">🚫</span>
          </div>
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground mb-4">
            You don't have permission to access this section. 
            Please contact your administrator if you believe this is an error.
          </p>
          <a 
            href="/admin/dashboard" 
            className="text-primary hover:underline text-sm"
          >
            ← Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminRoute;
